import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../store";
import {
  getRoleNavigationByRole,
  updateRoleNavigation,
} from "../store/slices/userSlice";
import Button, { ButtonType } from "./Assets/Button";
import { NavItem, Navigations } from "../models/userData.model";
import { getAllNavigation } from "../store/slices/userSlice";
import NavigationAccessModal from "./NavigationAccessModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  roles: string[];
}

const UpdateRoleNavigationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  roles,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const allNavigation = useSelector(
    (state: RootState) => state.users.allNavigation,
  );

  const [selectedRole, setSelectedRole] = useState("");
  const [selected, setSelected] = useState<Record<string, NavItem[]>>({
    main: [],
    extras: [],
    frelit: [],
  });
  const [roleNav, setRoleNav] = useState<Navigations | null>(null);
  const [isAccessOpen, setIsAccessOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllNavigation());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedRole) return;

    const fetchData = async () => {
      const result = await dispatch(
        getRoleNavigationByRole(selectedRole),
      ).unwrap();

      const mapIds = (ids: string[] = []) =>
        ids
          .map((id) => allNavigation.find((n) => n._id === id))
          .filter((n): n is NavItem => !!n);

      setSelected({
        main: mapIds(result?.main),
        extras: mapIds(result?.extras),
        frelit: mapIds(result?.frelit),
      });
    };

    fetchData();
  }, [selectedRole, allNavigation, dispatch]);

  const toggleNav = (nav: NavItem, section: string) => {
    setSelected((prev) => {
      const updated = { ...prev };

      Object.keys(updated).forEach((key) => {
        updated[key] = updated[key].filter((n) => n._id !== nav._id);
      });

      const exists = prev[section].some((n) => n._id === nav._id);

      if (!exists) {
        updated[section] = [...updated[section], nav];
      }

      return updated;
    });
  };

  const handleUpdate = async () => {
    const convert = (arr: NavItem[]) =>
      arr.map((i) => i._id).filter((id): id is string => !!id);

    const leadsSelected = [
      ...(selected.main || []),
      ...(selected.extras || []),
      ...(selected.frelit || []),
    ].some((nav) => nav.component === "CRM");

    await dispatch(
      updateRoleNavigation({
        role: selectedRole,
        navigations: {
          main: convert(selected.main),
          extras: convert(selected.extras),
          frelit: convert(selected.frelit),
          notifications: leadsSelected ? notificationNav?._id || null : null,
          customerNotifications: leadsSelected
            ? customerNotificationNav?._id || null
            : null,
        },
      }),
    ).unwrap();

    onClose();
  };

  const isCRMSelected = [
    ...(selected.main || []),
    ...(selected.extras || []),
    ...(selected.frelit || []),
  ].some((nav) => nav.component === "CRM");

  const notificationNav = allNavigation.find(
    (n) => n.component === "NotificationPage",
  );

  const customerNotificationNav = allNavigation.find(
    (n) => n.component === "CustomerNotificationsPage",
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
          <h2 className="text-xl font-semibold text-gray-800">
            Update Role Navigation
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Select Role
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="w-full text-xs p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Choose a role</option>
              {roles.map((r) => (
                <option key={r} value={r}>
                  {r.capitalizeWords()}
                </option>
              ))}
            </select>
          </div>

          {["main", "extras", "frelit"].map((section) => (
            <div
              key={section}
              className="border rounded-xl p-4 bg-gray-50 shadow-sm"
            >
              <h3 className="text-sm font-semibold text-gray-700 mb-3 capitalize">
                {section} Navigation
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {allNavigation.map((nav) => {
                  const checked = selected[section].some(
                    (n) => n._id === nav._id,
                  );

                  return (
                    <div
                      key={nav._id}
                      onClick={() => toggleNav(nav, section)}
                      className={`cursor-pointer border rounded-lg px-3 py-2 flex items-center gap-2 transition
                    ${
                      checked
                        ? "bg-blue-50 border-blue-400 shadow-sm"
                        : "bg-white hover:border-blue-300"
                    }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        className="accent-blue-600 cursor-pointer"
                      />
                      <span className="text-xs text-gray-700">{nav.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="border rounded-lg bg-gray-50 p-4">
          <h3 className="font-semibold text-gray-700 mb-3">
            Notifications (Auto Assigned)
          </h3>

          <div className="grid grid-cols-2 gap-3">
            {[notificationNav, customerNotificationNav].map((nav) => {
              if (!nav) return null;

              return (
                <div
                  key={nav._id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border 
          ${
            isCRMSelected
              ? "bg-green-50 border-green-400"
              : "bg-gray-100 border-gray-300 opacity-60"
          }`}
                >
                  <input
                    type="checkbox"
                    checked={isCRMSelected}
                    readOnly
                    className="accent-green-600 cursor-not-allowed"
                  />
                  <span>{nav.label}</span>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-500 mt-2">
            Automatically enabled when Leads access is selected.
          </p>
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <Button type={ButtonType.Cancel} onClick={onClose}>
            Cancel
          </Button>
          <Button type={ButtonType.Submit} onClick={handleUpdate}>
            Update Access
          </Button>
        </div>
      </div>
      {roleNav && (
        <NavigationAccessModal
          isOpen={isAccessOpen}
          onClose={() => setIsAccessOpen(false)}
          navigations={roleNav}
          setNavigations={() => {}}
          onSave={handleUpdate}
        />
      )}
    </div>
  );
};

export default UpdateRoleNavigationModal;
